import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';

import { Button } from '../Button';
import { FadeView } from '../FadeView';
import { IconLink } from '../Icon';
import { LoadingDots } from '../LoadingDots';
import { QuantityStepper } from './QuantityStepper';

export const ButtonQuantityStepper = (props: ButtonQuantityStepperProps) => {
  const {
    onSubmit,
    iconName,
    isDisabled,
    isLoading,
    onStepperVisibilityChange,
    onQuantityIncrease,
    onQuantityDecrease,
    initialQuantity = 1,
    autoCloseTimeout = 2000,
    accessibilityLabel,
    accessibilityHint,
    loadingAccessibilityLabel,
    toggleTestID = 'stepper-toggle',
    ...rest
  } = props;

  // ─── State ─────────────────────────────────────────────────────────────────────

  const [showStepper, setShowStepper] = useState(false);
  const [quantity, setQuantity] = useState(initialQuantity);

  // ─── Callbacks ─────────────────────────────────────────────────────────────────────

  const openStepper = useCallback(async () => {
    setShowStepper(true);
  }, []);

  const closeStepper = useCallback(() => {
    setShowStepper(false);
  }, [setShowStepper]);

  const increaseQuantity = useCallback(
    (updatedQuantity: number) => {
      setQuantity(updatedQuantity);
      onQuantityIncrease?.(updatedQuantity);
    },
    [onQuantityIncrease],
  );

  const decreaseQuantity = useCallback(
    (updatedQuantity: number) => {
      setQuantity(updatedQuantity);
      onQuantityDecrease?.(updatedQuantity);
    },
    [onQuantityDecrease],
  );

  const handleSubmit = useCallback(
    (submittedQuantity: number) => {
      onSubmit(submittedQuantity);
      setShowStepper(false);
    },
    [onSubmit, setShowStepper],
  );

  useEffect(() => {
    onStepperVisibilityChange?.(showStepper);
  }, [showStepper, onStepperVisibilityChange]);

  const stepperChangesTimeout = useRef<ReturnType<typeof setTimeout>>();

  // If there is no action after specified time, hide the stepper if it's open and
  // call the onSubmit callback with the quantity.
  useEffect(() => {
    if (stepperChangesTimeout.current)
      clearTimeout(stepperChangesTimeout.current);

    if (showStepper) {
      // eslint-disable-next-line functional/immutable-data
      stepperChangesTimeout.current = setTimeout(() => {
        handleSubmit(quantity);
      }, autoCloseTimeout);
    }

    return () => {
      clearTimeout(stepperChangesTimeout.current);
    };
  }, [
    stepperChangesTimeout,
    quantity,
    showStepper,
    autoCloseTimeout,
    handleSubmit,
  ]);

  // ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <View
        testID="button-quantity-stepper-loading-dots"
        style={styles.loadingWrapper}
        accessibilityLabel={loadingAccessibilityLabel}
        accessibilityRole="alert"
      >
        <LoadingDots color={theme.colors.WHITE} />
      </View>
    );
  }

  if (!showStepper && iconName) {
    return (
      <IconLink
        name={iconName}
        iconSize={QUANTITY_VIEW_SIZE}
        onPress={openStepper}
        disabled={isDisabled}
        style={styles.button}
        testID={`${toggleTestID}-icon`}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
        accessibilityRole="button"
      />
    );
  }

  if (!showStepper) {
    return (
      <Button
        palette="primary"
        style={styles.quantityView}
        onPress={openStepper}
        disabled={isDisabled}
        testID={toggleTestID}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
      >
        {quantity > 99 ? '99+' : `${quantity}×`}
      </Button>
    );
  }

  return (
    <View style={styles.stepperWrapper}>
      <FadeView show={showStepper} style={styles.quantityStepper}>
        <QuantityStepper
          onQuantityDecrease={decreaseQuantity}
          onQuantityIncrease={increaseQuantity}
          initialQuantity={initialQuantity}
          onClose={closeStepper}
          {...rest}
        />
      </FadeView>
    </View>
  );
};

// ─── CONSTANTS ──────────────────────────────────────────────────────────────────

const QUANTITY_VIEW_SIZE = 40;
const STEPPER_WIDTH = 122;
const STEPPER_HEIGHT = 48;
const BORDER_RADIUS = 62;

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  quantityView: {
    alignItems: 'center',
    justifyContent: 'center',
    width: QUANTITY_VIEW_SIZE,
    height: QUANTITY_VIEW_SIZE,
    borderRadius: QUANTITY_VIEW_SIZE,

    // reset button padding
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  loadingWrapper: {
    borderRadius: BORDER_RADIUS,
    backgroundColor: theme.colors.GREEN_2,
    width: QUANTITY_VIEW_SIZE,
    height: QUANTITY_VIEW_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepperWrapper: {
    position: 'relative',
    zIndex: 100,
    height: QUANTITY_VIEW_SIZE,
  },
  quantityStepper: {
    justifyContent: 'center',
    position: 'absolute',
    top: QUANTITY_VIEW_SIZE / 2,
    left: -(QUANTITY_VIEW_SIZE * 1.5),
    backgroundColor: theme.colors.KALE,
    borderRadius: BORDER_RADIUS,
    width: STEPPER_WIDTH,
    height: STEPPER_HEIGHT,
    transform: [
      { translateX: -(STEPPER_WIDTH / 2) },
      { translateY: -(STEPPER_HEIGHT / 2) },
    ],
    overflow: 'hidden',
    ...theme.elevations['3'],
  },
});

type ButtonQuantityStepperProps = Readonly<{
  onSubmit: (quantity: number) => void;
  iconName?: React.ComponentProps<typeof IconLink>['name'];
  initialQuantity?: number;
  isDisabled?: boolean;
  isLoading?: boolean;
  onStepperVisibilityChange?: (isVisible: boolean) => void;
  autoCloseTimeout?: number;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  loadingAccessibilityLabel?: string;
  toggleTestID?: string;
}> &
  React.ComponentProps<typeof QuantityStepper>;
